import { Video, PublicationStatePublished, PublicationStateSealed } from '../../types'

export interface VideoLevelFields {
  name: string
  description: string
  // Series and Episode are the two-line title a VIEWER sees; Name is the
  // admin-facing label and is never shown in the player. A video with
  // neither set falls back to a single line from Name.
  series: string
  episode: string
  rotationAdjustment: number
  streamUrl: string
  language: string
}

export interface PersistedVideoFields {
  name: string
  description: string
  series?: string
  episode?: string
  rotation_adjustment?: number
  stream_url?: string
  language?: string
}

export const isLikelyHlsUrl = (url: string): boolean =>
  url === '' || (url.startsWith('https://') && url.includes('.m3u8'))

// Sealed in effect: explicitly sealed, or a published video whose correction
// window has passed. Mirrors PublicationSection's sealedInEffect (and, one
// level further down, api/pkg/data.IsSealed) — the same predicate, not a
// second one, so this can never disagree with what the countdown there
// shows. Unlike that component's live countdown, this doesn't correct for
// clock skew: it gates a one-time render decision on page load, not a
// ticking display, so the server's own recent response is good enough.
export function isVideoSealed(video: Pick<Video, 'publication_state' | 'publication'>): boolean {
  const info = video.publication
  if (!info) return false
  if (video.publication_state === PublicationStateSealed) return true
  if (video.publication_state !== PublicationStatePublished) return false
  const windowExpiresMs = info.window_expires ? new Date(info.window_expires).getTime() : NaN
  return !Number.isNaN(windowExpiresMs) && Date.now() > windowExpiresMs
}

export function videoLevelFieldsChanged(
  fields: VideoLevelFields,
  video: PersistedVideoFields,
): boolean {
  return (
    fields.name !== video.name ||
    fields.description !== video.description ||
    fields.series !== (video.series || '') ||
    fields.episode !== (video.episode || '') ||
    fields.rotationAdjustment !== (video.rotation_adjustment || 0) ||
    fields.streamUrl !== (video.stream_url || '') ||
    fields.language !== (video.language || 'en')
  )
}

// Container-level fields — slug, ordering, private — live on the container,
// not the video (see types.Container), so they get their own dirty-check
// entirely separate from videoLevelFieldsChanged above. Publishing a new
// version must not make the container form (or its Save button) think
// anything changed, and vice versa.
export interface ContainerLevelFields {
  slug: string
  ordering: number
  isPrivate: boolean
}

export interface PersistedContainerFields {
  slug: string
  ordering?: number
  private?: boolean
}

export function containerFieldsChanged(
  fields: ContainerLevelFields,
  container: PersistedContainerFields,
): boolean {
  return (
    fields.slug !== container.slug ||
    fields.ordering !== (container.ordering || 0) ||
    fields.isPrivate !== (container.private || false)
  )
}

export function shouldCommitVideoFields(
  fields: VideoLevelFields,
  video: PersistedVideoFields,
): boolean {
  if (!isLikelyHlsUrl(fields.streamUrl)) return false
  return videoLevelFieldsChanged(fields, video)
}
